import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { useI18n } from "../../i18n";
import { ErrorBanner } from "../../components/ui";

export function LoginPage() {
  const { user, loading, login } = useAuth();
  const { lang, setLang, t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const callbackError = params.get("error");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate("/", { replace: true });
  }, [loading, user, navigate]);

  const startLogin = async () => {
    setLoginError(null);
    setIsSubmitting(true);
    try {
      await login();
    } catch {
      setLoginError(t("loginStartError"));
      setIsSubmitting(false);
    }
  };

  return (
    <main className="relative flex min-h-screen overflow-hidden bg-slate-950 px-5 py-8 sm:items-center sm:justify-center">
      <div
        aria-hidden="true"
        className="absolute -left-40 top-0 h-96 w-96 rounded-full bg-brand-600/30 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-48 -right-32 h-[30rem] w-[30rem] rounded-full bg-cyan-500/20 blur-3xl"
      />
      <section className="relative grid w-full max-w-5xl overflow-hidden rounded-3xl border border-white/15 bg-white shadow-2xl shadow-black/30 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="hidden bg-brand-700 p-12 text-white lg:flex lg:flex-col">
          <div className="flex items-center gap-3 text-lg font-semibold">
            <Logo />
            <span>{t("appName")}</span>
          </div>
          <div className="my-auto max-w-md">
            <p className="mb-5 text-sm font-semibold uppercase tracking-[0.22em] text-brand-100">
              {t("loginEyebrow")}
            </p>
            <h1 className="text-4xl font-bold leading-tight">{t("loginHeroTitle")}</h1>
            <p className="mt-5 text-base leading-7 text-brand-100">{t("loginHeroDescription")}</p>
          </div>
          <p className="text-sm text-brand-100">{t("loginSecurityNote")}</p>
        </div>
        <div className="relative flex min-h-[570px] flex-col justify-center p-7 sm:p-12">
          <label className="absolute right-7 top-7 sm:right-12 sm:top-10">
            <span className="sr-only">{lang === "ko" ? "언어" : "Language"}</span>
            <select
              aria-label={lang === "ko" ? "언어" : "Language"}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 outline-none transition hover:border-brand-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
              onChange={(event) => setLang(event.target.value === "en" ? "en" : "ko")}
              value={lang}
            >
              <option value="ko">한국어</option>
              <option value="en">English</option>
            </select>
          </label>
          <div className="mb-10 lg:hidden">
            <div className="flex items-center gap-3 text-lg font-semibold text-brand-700">
              <Logo />
              <span>{t("appName")}</span>
            </div>
          </div>
          <div className="max-w-sm">
            <p className="text-sm font-semibold uppercase tracking-[0.16em] text-brand-700">
              {t("loginEyebrow")}
            </p>
            <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">
              {t("loginTitle")}
            </h2>
            <p className="mt-3 text-sm leading-6 text-slate-500">{t("loginDescription")}</p>
          </div>
          <div className="mt-8 max-w-sm space-y-4">
            {(callbackError || loginError) && (
              <ErrorBanner message={loginError ?? callbackError} />
            )}
            <button
              className="flex w-full items-center justify-center gap-3 rounded-xl bg-brand-600 px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-brand-600/25 transition hover:bg-brand-700 focus:outline-none focus:ring-4 focus:ring-brand-200 disabled:cursor-not-allowed disabled:opacity-60"
              disabled={isSubmitting}
              onClick={() => void startLogin()}
              type="button"
            >
              <img alt="" aria-hidden="true" className="h-6 w-6 rounded-md" src="/ComplianceSage.png" />
              {isSubmitting ? t("loginLoading") : t("login")}
            </button>
            <div className="flex items-start gap-2 rounded-xl bg-slate-50 px-4 py-3 text-xs leading-5 text-slate-500">
              <ShieldIcon />
              <p>{t("loginNotice")}</p>
            </div>
          </div>
          <p className="mt-10 text-xs text-slate-400">{t("loginSupport")}</p>
        </div>
      </section>
    </main>
  );
}

function Logo() {
  return (
    <img alt="" aria-hidden="true" className="h-10 w-10 rounded-xl" src="/ComplianceSage.png" />
  );
}

function ShieldIcon() {
  return (
    <svg aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" fill="none" viewBox="0 0 24 24">
      <path d="M12 3 5 6.5v5.25c0 4.6 3 7.9 7 9.25 4-1.35 7-4.65 7-9.25V6.5L12 3Z" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}
